'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type {
  EgressStrategy,
  ExternalNetworkDto,
  UpdateExternalNetworkDto,
} from '@terra/shared-contracts-external-network';
import { createSelectListCollection } from '../../../components/select';
import { SettingsEditDialog } from '../../settings/SettingsEditDialog';
import { useDraftOnOpen } from '../../application-general/useDraftOnOpen';
import { ExternalNetworkSettingsSelectField } from './ExternalNetworkSettingsSelectField';
import {
  buildEgressStrategyUpdates,
  getEgressStrategyOptions,
  isEgressStrategy,
  resolveConnectorDnsServerIps,
  type ConnectorDnsNetwork,
} from './external-settings.model';

export type ConnectorNetworkPickerRenderProps = {
  internalAccessNetworkId: string | null;
  onInternalAccessNetworkIdChange: (networkId: string | null) => void;
};

export type ExternalNetworkEgressStrategyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  surface: Pick<
    ExternalNetworkDto,
    'egressStrategy' | 'internalAccessNetworkId' | 'dnsServerIps'
  >;
  isConnectorScanEnabled: boolean;
  connectorNetworks: readonly ConnectorDnsNetwork[];
  isUpdating: boolean;
  onSubmit: (updates: UpdateExternalNetworkDto) => Promise<boolean>;
  renderConnectorNetworkPicker: (
    props: ConnectorNetworkPickerRenderProps,
  ) => ReactNode;
};

type EgressStrategyDraft = {
  egressStrategy: EgressStrategy;
  internalAccessNetworkId: string | null;
  dnsServerIps: string[];
};

export function ExternalNetworkEgressStrategyDialog({
  open,
  onOpenChange,
  surface,
  isConnectorScanEnabled,
  connectorNetworks,
  isUpdating,
  onSubmit,
  renderConnectorNetworkPicker,
}: ExternalNetworkEgressStrategyDialogProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalSettings.scanning',
  );
  const [draft, setDraft] = useDraftOnOpen<EgressStrategyDraft>({
    open,
    value: {
      egressStrategy: surface.egressStrategy,
      internalAccessNetworkId: surface.internalAccessNetworkId ?? null,
      dnsServerIps: surface.dnsServerIps ?? [],
    },
  });
  const collection = useMemo(
    () =>
      createSelectListCollection({
        items: getEgressStrategyOptions({ isConnectorScanEnabled }),
      }),
    [isConnectorScanEnabled],
  );
  const isConnectorSurface = surface.egressStrategy === 'CONNECTOR';
  const showConnectorNetworkPicker =
    isConnectorScanEnabled && draft.egressStrategy === 'CONNECTOR';

  const handleInternalAccessNetworkIdChange = (networkId: string | null) => {
    const dnsServerIps = resolveConnectorDnsServerIps({
      networks: connectorNetworks,
      networkId,
    });

    setDraft((current) =>
      current.internalAccessNetworkId === networkId &&
      current.dnsServerIps.join(',') === dnsServerIps.join(',')
        ? current
        : { ...current, internalAccessNetworkId: networkId, dnsServerIps },
    );
  };

  return (
    <SettingsEditDialog
      open={open}
      title={t('editEgressStrategyTitle')}
      onOpenChange={onOpenChange}
      onSubmit={() =>
        void onSubmit(buildEgressStrategyUpdates(draft)).then(
          (ok) => ok && onOpenChange(false),
        )
      }
      loading={isUpdating}
    >
      {isConnectorSurface ? null : (
        <ExternalNetworkSettingsSelectField
          label={t('egressStrategy')}
          collection={collection}
          value={draft.egressStrategy}
          onValueChange={(value) => {
            if (isEgressStrategy(value)) {
              setDraft((current) => ({ ...current, egressStrategy: value }));
            }
          }}
        />
      )}
      {showConnectorNetworkPicker
        ? renderConnectorNetworkPicker({
            internalAccessNetworkId: draft.internalAccessNetworkId,
            onInternalAccessNetworkIdChange:
              handleInternalAccessNetworkIdChange,
          })
        : null}
    </SettingsEditDialog>
  );
}
